import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Button,
    FormControl,
    FormLabel,
    Stack,
    SxProps,
    TextField,
} from '@mui/material';
import { headingFontBold, labelFontSemiBold, bodyFont, vars } from '../../../../../Theme';
import { IShareEmailErrorState, IShareEmailMainState } from '../ShareEmailDialog';
import {
    IEmailTemplate,
    MailSendOptionEnum,
} from '../../../../../redux/project/documents/sliceProjectShareEmail';
import InputEmail from '../../_parts/InputEmail';
import RadioBtnGroup from '../../_parts/RadioBtnGroup';
import SelectTemplate from '../../_parts/SelectTemplate';
import Message from '../../_parts/Message';
import { mdiInformationOutline, mdiAlertCircle, mdiChevronDown, mdiCogOutline } from '@mdi/js';
import AppIcon from '../../../Icons/AppIcon';
import AdvancedExport, { IExportAdditionalDocument } from '../../_parts/AdvancedExport';

/** Styles for FormLabel. */
const formLabelSx: SxProps = {
    ...headingFontBold,
    fontSize: '16px',
    color: vars.black,
    fontWeight: 700,
    marginBottom: '2px',

    '&.Mui-focused': {
        color: vars.black,
    },
};

/** Typography popover styles. */
const popoverSx: SxProps = {
    ...bodyFont,
    fontSize: '14px',
    color: vars.font,
    fontWeight: 600,
    lineHeight: 'normal',
    fontStyle: 'normal',
    maxWidth: '256px',
};

/** Typography caption styles. */
const captionSx: SxProps = {
    ...labelFontSemiBold,
    fontSize: '14px',
    color: vars.font,
    fontWeight: 500,
    lineHeight: 'normal',
    fontStyle: 'normal',
};

/** Styles for CC and BCC buttons. */
const showCCandBCCButtonSx: SxProps = {
    ...headingFontBold,
    fontSize: '14px',
    color: vars.black,
    fontWeight: 700,

    '&.Mui-focused': {
        color: vars.black,
    },
    minWidth: '10px',
    padding: '0px 12px',

    '&:last-of-type': {
        paddingRight: '0px',
    },

    '&:hover': {
        backgroundColor: 'unset',
    },
};

const textFieldSx: SxProps = {
    input: { ...bodyFont, p: 0 },

    '& .MuiOutlinedInput-root': {
        '&.Mui-focused fieldset': {
            border: `2px solid ${vars.black}`,
        },
    },
    '& .MuiFormHelperText-root': {
        marginLeft: 'unset',
    },

    '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall': {
        py: '6.5px',
        pl: '12px',
    },
};

const accordionSx = {
    '&:before': {
        display: 'none',
    },
    boxShadow: 'none',
};
const accordionSummarySx = {
    '& .MuiAccordionSummary-content': { m: 0, gap: '8px', alignItems: 'center' },
    '& .MuiAccordionSummary-content.Mui-expanded': {
        m: 0,
    },
    p: '0px',
    m: 0,
    minHeight: '40px',
    fontFamily: headingFontBold,
    fontSize: '16px',
};

export default ({
    mainState,
    handleChange,
    showErrorState,
    handleChangeTemplate,
    templates,
}: IShareEmailContentProps) => {
    const { t } = useTranslation('translations', { keyPrefix: 'shareEmailDialog' });

    const [showCC, setShowCC] = React.useState(false);
    const [showBCC, setShowBCC] = React.useState(false);

    useEffect(() => {
        mainState.copyTo.length > 0 && setShowCC(true);
    }, [mainState.copyTo]);

    useEffect(() => {
        mainState.blindCopyTo.length > 0 && setShowBCC(true);
    }, [mainState.blindCopyTo]);

    return (
        <Stack spacing={'12px'}>
            <FormControl>
                <FormLabel sx={formLabelSx}>{t('dispatch')}</FormLabel>
                <RadioBtnGroup
                    horizontal={true}
                    onSelect={key => handleChange({ field: 'mailSendOption', value: key })}
                    value={mainState.mailSendOption}
                    options={[
                        {
                            key: MailSendOptionEnum.DownloadLink,
                            label: (
                                <AppIcon
                                    {...{
                                        path: mdiInformationOutline,
                                        tooltipPlacement: 'top-start',
                                        withLabel: true,
                                        labelSx: { ...captionSx },
                                        wrapperSx: {
                                            flexDirection: 'row-reverse',
                                        },
                                        label: t('downloadLink'),
                                        type: 'white',
                                        tooltipText: (
                                            <AppIcon
                                                {...{
                                                    path: mdiInformationOutline,
                                                    withLabel: true,
                                                    labelSx: { ...popoverSx },
                                                    wrapperSx: { alignItems: 'flex-start' },
                                                    label: t('downloadLinkPopover'),
                                                }}
                                            />
                                        ),
                                    }}
                                />
                            ),
                        },
                        {
                            key: MailSendOptionEnum.FileAttachment,
                            label: (
                                <AppIcon
                                    {...{
                                        path: mdiInformationOutline,
                                        tooltipPlacement: 'top-end',
                                        withLabel: true,
                                        labelSx: { ...captionSx },
                                        wrapperSx: {
                                            flexDirection: 'row-reverse',
                                        },
                                        label: t('fileAttachment'),
                                        type: 'white',
                                        tooltipText: (
                                            <AppIcon
                                                {...{
                                                    path: mdiInformationOutline,
                                                    withLabel: true,
                                                    labelSx: { ...popoverSx },
                                                    wrapperSx: { alignItems: 'flex-start' },
                                                    label: t('fileAttachmentPopover'),
                                                }}
                                            />
                                        ),
                                    }}
                                />
                            ),
                        },
                    ]}
                    groupSx={{ justifyContent: 'space-between', px: 0 }}
                />
            </FormControl>
            <FormControl>
                <FormLabel sx={formLabelSx}>{t('emailTemplate')}</FormLabel>
                <SelectTemplate
                    selectedTemplate={mainState.selectedTemplate}
                    templates={templates}
                    handleChangeTemplate={handleChangeTemplate}
                />
            </FormControl>
            <FormControl required>
                <Stack
                    direction='row'
                    sx={{ justifyContent: 'space-between' }}
                >
                    <Stack direction='row' spacing={1}>
                        <FormLabel sx={formLabelSx}>{t('recipient')}</FormLabel>
                        <AppIcon path={mdiInformationOutline} tooltipText={t('recipientInfotext')} tooltipPlacement='top-start' />
                    </Stack>
                    <Stack direction={'row'} spacing={1}>
                        {!showBCC && (
                            <Button
                                onClick={() => setShowBCC(true)}
                                size='small'
                                sx={showCCandBCCButtonSx}
                            >
                                {t('bcc')}
                            </Button>
                        )}
                        {!showCC && (
                            <Button
                                onClick={() => setShowCC(true)}
                                size='small'
                                sx={showCCandBCCButtonSx}
                            >
                                {t('cc')}
                            </Button>
                        )}
                    </Stack>
                </Stack>
                <InputEmail
                    field='sendTo'
                    emails={mainState.sendTo}
                    setEmails={handleChange}
                    placeholder={t('enterRecipients')}
                    hasError={showErrorState.sendTo}
                    errorMessage={t('errorInput')}
                />
            </FormControl>
            {showBCC && (
                <FormControl>
                    <FormLabel sx={formLabelSx}>{t('bcc')}</FormLabel>
                    <InputEmail
                        emails={mainState.blindCopyTo}
                        setEmails={handleChange}
                        field='blindCopyTo'
                        placeholder={t('enterBCC')}
                    />
                </FormControl>
            )}
            {showCC && (
                <FormControl>
                    <FormLabel sx={formLabelSx}>{t('cc')}</FormLabel>
                    <InputEmail
                        emails={mainState.copyTo}
                        setEmails={handleChange}
                        field='copyTo'
                        placeholder={t('enterCC')}
                    />
                </FormControl>
            )}
            <FormControl required>
                <FormLabel sx={formLabelSx}>{t('subject')}</FormLabel>
                <TextField
                    size='small'
                    sx={textFieldSx}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleChange({ field: 'subject', value: e.target.value })
                    }
                    placeholder={t('enterSubject')}
                    value={mainState.subject}
                    error={showErrorState.subject}
                    helperText={
                        showErrorState.subject && (
                            <Message
                                text={t('errorInput')}
                                icon={mdiAlertCircle}
                                iconSize={0.75}
                                color={vars.errorDialogRed}
                                typographySx={{
                                    fontWeight: 400,
                                    fontSize: '12px',
                                    ...bodyFont,
                                }}
                                wrapperSx={{
                                    border: 'none',
                                    p: 0,
                                    m: 0,
                                    gap: 0.5,
                                }}
                            />
                        )
                    }
                />
            </FormControl>
            <FormControl>
                <FormLabel sx={formLabelSx}>{t('emailText')}</FormLabel>
                <TextField
                    size='small'
                    sx={textFieldSx}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleChange({ field: 'emailText', value: e.target.value })
                    }
                    placeholder={t('enterEmailText')}
                    value={mainState.emailText}
                    multiline
                />
            </FormControl>
            <FormControl required>
                <Stack direction={'row'} spacing={'8px'}>
                    <FormLabel sx={formLabelSx}>{t('replyTo')} </FormLabel>
                    <AppIcon
                        {...{
                            path: mdiInformationOutline,
                            tooltipPlacement: 'top-start',
                            type: 'white',
                            tooltipText: (
                                <AppIcon
                                    {...{
                                        path: mdiInformationOutline,
                                        withLabel: true,
                                        labelSx: { ...popoverSx },
                                        wrapperSx: { alignItems: 'flex-start' },
                                        label: t('replyToPopover'),
                                    }}
                                />
                            ),
                        }}
                    />
                </Stack>
                <InputEmail
                    emails={mainState.replyTo}
                    setEmails={handleChange}
                    field='replyTo'
                    placeholder={t('enterReplyTo')}
                    hasError={showErrorState.replyTo}
                    errorMessage={t('errorInput')}
                />
            </FormControl>
            <Accordion disableGutters sx={accordionSx}>
                <AccordionSummary
                    expandIcon={<AppIcon path={mdiChevronDown} color={vars.font} size={1} />}
                    sx={accordionSummarySx}
                >
                    <AppIcon path={mdiCogOutline} color={vars.font} size={1} />
                    {t('advancedExportSettings')}
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0, pt: '8px' }}>
                    <AdvancedExport
                        mainState={{
                            documentTypeName: mainState.documentTypeName,
                            includeInExport: mainState.includeInExport,
                            additionalFiles: mainState.additionalFiles,
                        }}
                        handleChange={handleChange}
                        showErrorState={{ documentTypeName: showErrorState.documentTypeName }}
                    />
                </AccordionDetails>
            </Accordion>
        </Stack>
    );
};

interface IShareEmailContentProps {
    mainState: IShareEmailMainState;
    handleChange: ({
        field,
        value,
    }: {
        field: string;
        value: string | string[] | IExportAdditionalDocument | IExportAdditionalDocument[];
    }) => void;
    showErrorState: IShareEmailErrorState;
    handleChangeTemplate: (template: IEmailTemplate) => void;
    templates: IEmailTemplate[];
}
